/**
 * Product data adapters — map various data sources into HecProduct.
 *
 * Each data source (CIO browse, SCAPI search hit, SCAPI product detail)
 * provides a different subset of fields. These adapters normalize them
 * into the unified HecProduct type so HecProductTile works regardless
 * of where the data came from.
 */
import type { ProductHitWithSwatches } from "~/shop/products/productSearch.server";

import type { HecInventory, HecProduct } from "~/hec/types/product";
import { buildProductUrl } from "~/hec/utils/product-slug";
import type { ProductSearchHit } from "~/lib/app.types";
import type { CIOBrowseProduct } from "~/routes/resources/cio-browse";
import { getProductHitImage } from "~/shop/utils/image";

/**
 * Maps a CIO browse product (enriched with SCAPI custom attrs) into HecProduct.
 * Used by HomepageCarouselGrid and other CIO-powered carousels.
 */
export function cioBrowseToHecProduct(cio: CIOBrowseProduct): HecProduct {
  return {
    id: cio.id,
    name: cio.name,
    price: cio.price,
    priceMax: cio.priceMax,
    image_url: cio.imageUrl,
    url: cio.url,
    c_badging: cio.c_badging,
    c_badgeDisplayFrom: cio.c_badgeDisplayFrom,
    c_badgeDisplayTo: cio.c_badgeDisplayTo,
    c_secondBadging: cio.c_secondBadging,
    c_secondBadgeDisplayFrom: cio.c_secondBadgeDisplayFrom,
    c_secondBadgeDisplayTo: cio.c_secondBadgeDisplayTo,
    c_isEligible: cio.c_isEligible,
    rating: cio.c_averageRating,
    reviewCount: cio.c_totalReviews,
    c_plpPromotions: cio.c_plpPromotions,
    c_plpPromotionsDisplayFrom: cio.c_plpPromotionsDisplayFrom,
    c_plpPromotionsDisplayTo: cio.c_plpPromotionsDisplayTo,
    c_crossPromoMessaging: cio.c_crossPromoMessaging,
    c_crossPromoMessagingDisplayFrom: cio.c_crossPromoMessagingDisplayFrom,
    c_crossPromoMessagingDisplayTo: cio.c_crossPromoMessagingDisplayTo,
    c_compareToSKU: cio.c_compareToSKU,
    c_ownerClass: cio.c_ownerClass,
    orderable: cio.orderable,
    available: cio.orderable,
    // SFCC-12175 W3: real GA4 item_category for select_item on every
    // CIO-browse-fed tile surface (recs, carousels, hybrid-PDP grids). Mirrors
    // the `productHitToHecProduct` line below. Undefined when the caller didn't
    // opt into resolution.
    categoryPath: cio.categoryPath,
    c_saleprice:
      cio.salePrice != null && cio.salePrice !== cio.price
        ? cio.salePrice
        : undefined,
    type: {
      master: cio.productType === "master",
      bundle: cio.productType === "bundle",
      set: cio.productType === "set",
    },
  };
}

/**
 * Maps a SCAPI ProductSearchHit (with swatches) into HecProduct.
 * Used by PLP, search results, and any SCAPI-powered product grid.
 *
 * The c_productInfo field contains the full SCAPI product data including
 * custom attributes. We extract what we need for the unified tile.
 */
export function productHitToHecProduct(
  hit: ProductHitWithSwatches,
): HecProduct {
  const info = hit.c_productInfo as Record<string, unknown> | undefined;
  const hitAny = hit as Record<string, unknown>;

  const salesPrice = hit.c_saleprice ?? hit.c_pricing?.salesprice;
  const standardPrice = hit.c_pricing?.standardprice;
  const image = hit.image ?? getProductHitImage(hit);

  // CIO suggests a variant per master (representedProduct.id); enrichHitsWithScapi
  // resolves it to variationValues on `suggestedVariantValues`. Deep-link to the
  // master with those values as query params so the PDP lands pre-selected on the
  // suggested variant. Canonical stays the master (seo.product.server strips the
  // params), and the PDP's variant URL-sync uses the same param shape. Falls back
  // to the bare master URL when no suggestion was resolved (e.g. standard products).
  const suggestedVariantValues = hitAny.suggestedVariantValues as
    | Record<string, string>
    | undefined;
  const baseUrl = buildProductUrl(hit.productId, hit.productName);
  const url =
    suggestedVariantValues && Object.keys(suggestedVariantValues).length > 0
      ? `${baseUrl}?${new URLSearchParams(suggestedVariantValues).toString()}`
      : baseUrl;

  return {
    id: hit.productId,
    productId: hit.productId,
    name: hit.productName ?? "",
    productName: hit.productName,
    price: hit.price ?? standardPrice,
    priceMax: hit.priceMax,
    image_url: image?.link,
    image,
    url,
    orderable: hit.orderable,
    available: hit.orderable,
    currency: hit.currency,
    type: hit.productType,
    productType: hit.productType,
    representedProduct: hit.representedProduct,
    // SFCC-11590 QA follow-up — propagate the master-id signals so downstream
    // surfaces (e.g. RecsCarouselTile's Quick View gate) can detect variants
    // of masters, not just masters themselves. CIO recs pods can return
    // either master IDs or variant SKUs; the gate must catch both shapes.
    // `master.masterId` is set by SCAPI on variant Product responses; the
    // `c_parentMasterProductID` custom attr mirrors it. Either is sufficient
    // to identify a variant. `masterId` here covers older shapes (some hit
    // sources flatten the field one level up). See:
    // .claude/.../project_wishlist_master_variant_gotchas.md for the broader
    // master/variant rules.
    master: hitAny.master as HecProduct["master"],
    c_parentMasterProductID: hitAny.c_parentMasterProductID as
      | string
      | undefined,
    masterId: hitAny.masterId as string | undefined,
    variationAttributes: hit.variationAttributes?.map(va => ({
      id: va.id,
      name: va.name ?? va.id,
      values: (va.values ?? []).map(v => ({
        name: v.name ?? v.value ?? "",
        value: v.value ?? "",
        orderable: v.orderable,
      })),
    })),

    // Sale price
    c_saleprice: salesPrice,
    c_pricing: hit.c_pricing,

    // Badges and eligibility — set on top-level hit by the loader (not in c_productInfo)
    c_badging: hitAny.c_badging as string | undefined,
    c_badgeDisplayFrom: hitAny.c_badgeDisplayFrom as string | undefined,
    c_badgeDisplayTo: hitAny.c_badgeDisplayTo as string | undefined,
    c_secondBadging: hitAny.c_secondBadging as string | undefined,
    c_secondBadgeDisplayFrom: hitAny.c_secondBadgeDisplayFrom as
      | string
      | undefined,
    c_secondBadgeDisplayTo: hitAny.c_secondBadgeDisplayTo as string | undefined,
    c_isEligible: hitAny.c_isEligible as boolean | undefined,
    rating: info?.c_averageRating as number | undefined,
    reviewCount: info?.c_totalReviews as number | undefined,
    c_plpPromotions: info?.c_plpPromotions as string | undefined,
    c_plpPromotionsDisplayFrom: info?.c_plpPromotionsDisplayFrom as
      | string
      | undefined,
    c_plpPromotionsDisplayTo: info?.c_plpPromotionsDisplayTo as
      | string
      | undefined,
    c_crossPromoMessaging: info?.c_crossPromoMessaging as string | undefined,
    c_crossPromoMessagingDisplayFrom: info?.c_crossPromoMessagingDisplayFrom as
      | string
      | undefined,
    c_crossPromoMessagingDisplayTo: info?.c_crossPromoMessagingDisplayTo as
      | string
      | undefined,
    c_compareToSKU: info?.c_compareToSKU as string | undefined,
    c_ownerClass: info?.c_ownerClass as string | undefined,
    c_oosDisplayOverride: info?.c_oosDisplayOverride as boolean | undefined,
    c_brandURL: info?.c_brandURL as string | undefined,
    c_youtubeID: info?.c_youtubeID as string | undefined,
    inventory: info?.inventory as HecInventory | undefined,
    compareToData: hitAny.compareToData as
      | { name: string; url: string; formattedPrice: string }
      | undefined,
    // SFCC-12141: real GA4 item_category for select_item (was hardcoded blank
    // in product-tile.tsx's handleProductClick).
    categoryPath: hitAny.categoryPath as string | undefined,
    variationValues: info?.variationValues as
      | Record<string, string>
      | undefined,
    c_hazmat: hit.c_hazmat,
    c_restrictedbyAnyState: hit.c_restrictedbyAnyState,
    c_restrictedStates: hit.c_restrictedStates,
  };
}
